import MonitorIcon from '~/shared/assets/icons/monitor.svg';
import StorageIcon from '~/shared/assets/icons/storage.svg';
import CurrentUtilizationIcon from '~/shared/assets/icons/current-utilization.svg';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';
import KPICard from '~/shared/components/KPICard';
import { KPIHeader, KPISkeletonLoader } from '~/shared/components/kpi';

import AutoscalingActivationCard from './AutoscalingActivationCard';
import { useFleetManagerKpiData } from './hooks/useFleetManagerKpiData';

interface StorageSummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subText: string;
  bottomLeftContent?: React.ReactNode;
  bottomRightContent?: React.ReactNode;
}

export default function KPICards() {
  const { values, loading } = useFleetManagerKpiData();

  return (
    <div className="w-full flex gap-[10px]">
      <StorageSummaryCard
        icon={StorageIcon}
        title="Total storage"
        subText="All volumes in account"
        bottomLeftContent={
          <SummaryValue
            loading={loading.isTotalCardLoading}
            value={values.totalDetectedSize}
            skeletonWidth={88}
          />
        }
        bottomRightContent={
          <SummaryTag loading={loading.isTotalCardLoading} value={values.totalDetectedCount} />
        }
      />
      <StorageSummaryCard
        icon={MonitorIcon}
        title="Monitored storage"
        subText="Volumes monitored by Datafy"
        bottomLeftContent={
          <SummaryValue
            loading={loading.isMonitoredCardLoading}
            value={values.monitoredStorageSize}
            skeletonWidth={90}
          />
        }
        bottomRightContent={
          <SummaryTag
            loading={loading.isMonitoredCardLoading}
            value={values.monitoredStorageCount}
          />
        }
      />
      <AutoscalingActivationCard
        currentSize={values.managedSize}
        fullPotentialSize={values.fullPotentialSize}
        managedVolumes={values.managedVolumes}
        potentiallySupportedVolumes={values.potentiallySupportedVolumes}
        progressPercentage={values.progressPercentage}
        loading={loading.isPotentialCardLoading}
      />
      <StorageSummaryCard
        icon={CurrentUtilizationIcon}
        title="Current Utilization"
        subText="Of monitored volumes"
        bottomLeftContent={
          <SummaryValue
            loading={loading.isUtilizationCardLoading}
            value={values.currentUtilizationValue}
            skeletonWidth={90}
          />
        }
      />
    </div>
  );
}

function StorageSummaryCard({
  icon: Icon,
  title,
  subText,
  bottomLeftContent,
  bottomRightContent,
}: StorageSummaryCardProps) {
  return (
    <KPICard className="inline-flex flex-col justify-between w-full">
      <KPIHeader icon={Icon} title={title} subtitle={subText} />
      <div className="flex gap-3 items-end justify-between font-family-outfit">
        {bottomLeftContent && <div>{bottomLeftContent}</div>}
        {bottomRightContent && <div className="ml-auto">{bottomRightContent}</div>}
      </div>
    </KPICard>
  );
}

function SummaryValue({
  loading,
  value,
  skeletonWidth,
}: {
  loading: boolean;
  value: string;
  skeletonWidth: number;
}) {
  return (
    <div className="flex items-center">
      {loading ? (
        <KPISkeletonLoader width={skeletonWidth} />
      ) : (
        <Typography variant="H3-resp" fontWeight={600} color="text-primary-900">
          {value}
        </Typography>
      )}
    </div>
  );
}

function SummaryTag({ loading, value }: { loading: boolean; value: string }) {
  return (
    <div className="flex items-center justify-end">
      {loading ? (
        <KPISkeletonLoader width={92} variant="tag" />
      ) : (
        <Tag variant="kpi" size="kpi" text={value} showFullText />
      )}
    </div>
  );
}
